/** Canadian Shopify App API feed IDs verified in Merchant Center 657481079.
 * The approved Canadian public release maps only authoritative product/variant pairs; protected events retain their existing SKU payload.
 */
export interface MerchantTrackingContext {
  hostname?: string;
  releaseVersion?: string;
  path?: string;
  productHandle?: string;
  brand?: string;
}
export interface MerchantItemIdentity { item_id: string; item_sku?: string; }
const PUBLIC_HOSTS = ['www.raffi-jewellers.ca', 'raffi-jewellers.ca'];
// CartFields already returns each product's own title, but not its handle/vendor.
// A supported brand prefix is positive eligibility only; IDs always come from Shopify.
const WATCH_TITLE_PREFIX = /^(?:tudor|omega|z[eé]nith|breitling|grand\s+seiko|longines|oris|rado|mido|montblanc|gucci|fr[eé]d[eé]rique\s+constant|hamilton|movado|bulova|accutron|citizen|seiko|tissot)(?=$|[\s:®™–—-])/i;

function resourceId(value: unknown, resource: 'Product' | 'ProductVariant'): string | null {
  if (typeof value === 'number') return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
  if (typeof value !== 'string') return null;
  if (/^[1-9][0-9]*$/.test(value)) return value;
  const match = new RegExp(`^gid://shopify/${resource}/([1-9][0-9]*)$`).exec(value);
  return match ? match[1] : null;
}

/** Only the approved public release can use feed IDs; query parameters cannot enable it. */
export function merchantTrackingEnabled(context: MerchantTrackingContext = {}, product: any = {}): boolean {
  if (!PUBLIC_HOSTS.includes(context.hostname || '') || context.releaseVersion !== '20261008') return false;
  const decode = (value: unknown) => { try { return decodeURIComponent(String(value || '')); } catch (_) { return String(value || ''); } };
  return ![context.path, context.productHandle, context.brand, product?.handle, product?.brand, product?.vendor, product?.title].some(value => /rolex|tag(?:[+\s_-]|%20)*heuer/i.test(decode(value)));
}

export function merchantItemIdentity(product: any, variant: any, context: MerchantTrackingContext = {}): MerchantItemIdentity {
  const legacy = { item_id: variant?.sku };
  if (!merchantTrackingEnabled(context, product)) return legacy;
  const decode = (value: unknown) => { try { return decodeURIComponent(String(value || '')); } catch (_) { return String(value || ''); } };
  const path = decode(context.path);
  if (/^\/(?:fr\/)?rolex(?:\/|$)/i.test(path)) return legacy;
  const identities = [product?.handle, product?.brand, product?.vendor, context.productHandle, context.brand];
  const knownOwnTitle = [product?.title, variant?.product?.title].some(value => typeof value === 'string' && WATCH_TITLE_PREFIX.test(value.trim()));
  // Unknown cart products keep their SKU. Never borrow the selected PDP's identity for another line.
  if (!identities.some(value => typeof value === 'string' && value.trim()) && !knownOwnTitle) return legacy;
  if ([...identities, product?.title, variant?.product?.title, variant?.title, path].some(value => /rolex|tag(?:[+\s_-]|%20)*heuer/i.test(decode(value)))) return legacy;
  const productId = resourceId(product?.shopifyProductId || product?.id || variant?.product?.id, 'Product');
  const variantProductId = resourceId(variant?.product?.id, 'Product');
  const variantId = resourceId(variant?.id, 'ProductVariant');
  // Never manufacture a feed ID from conflicting product/variant provenance.
  if (!productId || !variantId || (variantProductId && variantProductId !== productId)) return legacy;
  return { item_id: `shopify_CA_${productId}_${variantId}`, item_sku: legacy.item_id };
}

/** Read the server-owned published-release marker; a query string cannot opt another host in. */
export function browserMerchantTrackingContext(): MerchantTrackingContext {
  if (typeof document === 'undefined' || typeof window === 'undefined') return {};
  return {
    hostname: window.location.hostname,
    path: window.location.pathname,
    releaseVersion: document.querySelector<HTMLMetaElement>('meta[name="raffi-native-release"]')?.content,
  };
}
